import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentRefinements, useInstantSearchContext } from 'react-instantsearch';
import * as autocompleteCore from '@algolia/autocomplete-core';
import * as autocompletePresetAlgolia from '@algolia/autocomplete-preset-algolia';
import { type SearchClient } from '@algolia/autocomplete-preset-algolia';
import client from '@searchkit/instantsearch-client';

import { type Repub } from '@cx-modules/constants';

import { type Facet } from '~/components/BettingInsights/components/Search/AutocompleteDropdown.tsx';
import { type BettingInsightsUiSearchAttribute } from '~/configuration/types';

const { createAutocomplete } = autocompleteCore;
const { getAlgoliaFacets } = autocompletePresetAlgolia;

// Constants
const DEBOUNCE_DELAY_MS = 300;
const MAX_FACET_HITS = 1;
const INITIAL_ACTIVE_INDEX = -1;

// Type definitions
type HelperState = {
  facetFilters?: string[];
  filters?: string;
};

type InstantSearchHelper = {
  search: () => void;
  setQueryParameter: (key: string, value: any) => void;
  state?: HelperState;
};

// Helper functions
function buildTagFilters(tags: string[], attributes: string[], operator: 'AND' | 'OR'): string {
  const tagGroups = tags.map(tag => attributes.map(facet => `${facet}:"${tag}"`).join(' OR '));
  return tagGroups
    .map(group => `(${group})`)
    .reduce((acc, wrapped, idx) => (idx > 0 ? `${acc} ${operator} ${wrapped}` : wrapped), '');
}

function combineFilters(...filters: (string | undefined)[]): string {
  return filters.filter(Boolean).join(' AND ');
}

function createSearchClient({ repub }: { repub: Repub }) {
  const searchClient = client({
    headers: {
      'OC-Repub': repub,
    },
    url: '/api/cx/v2/search',
  });

  // @ts-ignore
  searchClient.transporter = {
    headers: {
      'x-algolia-application-id': 'NULL',
      'x-algolia-api-key': 'NULL',
    },
  };

  return searchClient as unknown as SearchClient;
}

export const useSearchWithAutocomplete = ({
  indexName,
  inputRef,
  repub,
  searchAttributes,
  searchOperator = 'AND',
  setInputValue,
  setIsDropdownVisible,
}: {
  indexName: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  repub: Repub;
  searchAttributes: BettingInsightsUiSearchAttribute[];
  searchOperator?: 'AND' | 'OR';
  setInputValue: (value: string) => void;
  setIsDropdownVisible: (value: boolean) => void;
}) => {
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: ['eventDisplayName'],
  });
  const { helper } = useInstantSearchContext();
  const searchClient = useMemo(() => createSearchClient({ repub }), [repub]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(INITIAL_ACTIVE_INDEX);
  const initialFiltersRef = useRef<string | undefined>(undefined);
  const previousSelectedTagsRef = useRef<string[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Debounce helper using ref to fix module-level timer bug
  const debounceSources = useCallback(<T>(sources: T[], delay: number): Promise<T[]> => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    return new Promise(resolve => {
      debounceTimerRef.current = setTimeout(() => resolve(sources), delay);
    });
  }, []);

  const handleClearSearchResults = useCallback(() => {
    setSearchResults([]);
  }, []);

  // Extract getItems logic for readability
  const buildFacetQueries = useCallback(
    (query: string) => {
      const helperState = (helper as unknown as InstantSearchHelper)?.state;
      const stateFilters = helperState?.filters;
      const stateFacetFilters = helperState?.facetFilters;

      const eventDisplayNameFilter =
        currentRefinements.length > 0 && currentRefinements[0].refinements.length > 0
          ? `eventDisplayName:"${currentRefinements[0].refinements[0].value}"`
          : undefined;

      const combinedFilters = combineFilters(stateFilters, eventDisplayNameFilter);

      return getAlgoliaFacets<Facet>({
        searchClient,
        queries: searchAttributes.map((facet: string) => ({
          indexName,
          facet,
          params: {
            facetName: facet,
            facetQuery: query,
            maxFacetHits: MAX_FACET_HITS,
            ...(combinedFilters ? { filters: combinedFilters } : {}),
            ...(stateFacetFilters ? { facetFilters: stateFacetFilters } : {}),
          },
          type: 'facet' as const,
        })),
        transformResponse(response) {
          const validResults = response.results.filter(result => result && 'facetHits' in result);

          const seenValues = new Set<string>();
          return validResults.map(result =>
            result.facetHits
              .filter((hit: { count: number; highlighted?: string; value: string }) => {
                if (seenValues.has(hit.value)) {
                  return false;
                }
                seenValues.add(hit.value);
                return true;
              })
              .map((hit: { count: number; highlighted?: string; value: string }) => ({
                count: hit.count,
                label: hit.value,
                value: hit.value,
                _highlightResult: {
                  label: {
                    value: hit.highlighted || hit.value,
                    matchLevel: 'full',
                    matchedWords: [],
                  },
                },
              })),
          );
        },
      });
    },
    [helper, currentRefinements, searchClient, indexName, searchAttributes],
  );

  useEffect(() => {
    if (!helper) return;
    if (initialFiltersRef.current === undefined) {
      const helperState = (helper as unknown as InstantSearchHelper)?.state;
      initialFiltersRef.current = helperState?.filters;
    }
  }, [helper]);

  useEffect(() => {
    if (!helper) return;

    // Only search if selectedTags actually changed in value, not just reference
    const tagsChanged =
      selectedTags.length !== previousSelectedTagsRef.current.length ||
      selectedTags.some((tag, index) => tag !== previousSelectedTagsRef.current[index]);

    if (!tagsChanged) return;

    previousSelectedTagsRef.current = selectedTags;

    const tagFilters = buildTagFilters(selectedTags, searchAttributes, searchOperator);
    const combinedFilters = combineFilters(initialFiltersRef.current, tagFilters);

    helper.setQueryParameter(
      'filters',
      selectedTags.length > 0 ? combinedFilters : initialFiltersRef.current || undefined,
    );
    helper.setQueryParameter('page', 0);
    helper.search();
  }, [helper, selectedTags, searchAttributes, searchOperator]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, onEnter: () => void) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, searchResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, INITIAL_ACTIVE_INDEX));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onEnter();
      }
    },
    [searchResults.length],
  );

  const autocomplete = useMemo(
    () =>
      createAutocomplete<Facet, React.FormEvent, React.MouseEvent, React.KeyboardEvent>({
        onReset() {
          setActiveIndex(INITIAL_ACTIVE_INDEX);
          setSearchResults([]);
          setSelectedTags([]);
          setIsDropdownVisible(false);
          setInputValue('');
          autocomplete.setQuery('');
        },
        onStateChange({ state }) {
          const results = state.collections.flatMap(collection => collection.items.map(item => item.label));
          setInputValue(state.query);
          setSearchResults(results);
          setIsDropdownVisible(results.length > 0);
        },
        onSubmit({ state }) {
          setSelectedTags(tags => [...tags, state.query]);
          setActiveIndex(INITIAL_ACTIVE_INDEX);
          setSearchResults([]);
          setIsDropdownVisible(false);
          setInputValue('');
        },
        getSources() {
          return debounceSources(
            [
              {
                sourceId: 'searchAttributes',
                getItemInputValue({ item }: { item: Facet }) {
                  return item.label;
                },
                getItems({ query }: { query: string }) {
                  return buildFacetQueries(query);
                },
              },
            ],
            DEBOUNCE_DELAY_MS,
          );
        },
      }),
    [buildFacetQueries, debounceSources, setInputValue, setIsDropdownVisible],
  );

  return {
    activeIndex,
    autocomplete,
    getInputProps: () => autocomplete.getInputProps({ inputElement: inputRef.current }),
    handleClearSearchResults,
    handleKeyDown,
    searchResults,
    selectedTags,
    setActiveIndex,
    setSearchResults,
    setSelectedTags,
  };
};
